import { Injectable } from '@angular/core';
import { CanActivate } from '@angular/router';
import { EventId } from '@interfaces';
import { AccountTypes, FlowTypes, RejectedAccount, urlMapper } from '@interfaces/models';
import { select, Store } from '@ngrx/store';
import { LoggerCodes } from '@shared/modules/logger/logger';
import {
  closeSpinner,
  Go,
  LogError,
  LogInfo,
  selectItMultiReceivingToggle,
  State,
  TrackProcessError,
  UpdateDisplayedAccounts,
} from '@store/index';
import {
  selectClientAccountsLoaded,
  selectEligibleInternalTransferFromAccountsWithRejects,
} from '@store/selectors/client-accounts/client-accounts.selectors';
import { selectInternalTransferEligibility } from '@store/selectors/eligibility/internal-transfer/internal-transfer.eligibility.selectors';
import { selectInternalTransferToAccountType } from '@store/selectors/internal-transfer/internal-transfer.selectors';
import { CookieService } from 'ngx-cookie-service';
import { catchError, filter, map, Observable, of, switchMap, take, withLatestFrom } from 'rxjs';
@Injectable({ providedIn: 'root' })
export class DivorceEligibilityGuard implements CanActivate {
  rejectedFromAccounts: RejectedAccount[] = [];
  constructor(
    private globalStore: Store<State>,
    private cookieService: CookieService,
  ) {}
  canActivate(): Observable<boolean> {
    if (this.cookieService.get('routeGuard') === 'OFF') {
      return of(true);
    }
    return this.globalStore.pipe(
      // Ensure that the client accounts are loaded before proceeding with eligibility check
      select(selectClientAccountsLoaded),
      filter((loaded) => loaded),
      take(1),
      withLatestFrom(
        this.globalStore.select(selectItMultiReceivingToggle),
        this.globalStore.select(selectInternalTransferEligibility),
        this.globalStore.select(
          selectEligibleInternalTransferFromAccountsWithRejects(this.rejectedFromAccounts),
        ),
        this.globalStore.select(selectInternalTransferToAccountType),
      ),
      switchMap(([, isMultiReceivingToggleOn, payload, , accountToType]) => {
        // If multi IT toggle is ON, DivorceEligibilityMultiItGuard already handled eligibility — skip this guard
        if (isMultiReceivingToggleOn) return of(true);

        // If the client is eligible, update display accounts + log + close loading spinner and then activate page
        if (payload.isInternalTransferEligible) {
          if (!payload.fromAccounts) {
            throw new Error('BAD_PAYLOAD_FROM_ACCOUNTS');
          }
          this.globalStore.dispatch(
            UpdateDisplayedAccounts({
              payload: payload.fromAccounts,
            }),
          );
          this.globalStore.dispatch(
            LogInfo({
              payload: {
                eventId: 'divorce-guard--eligible-accounts-found',
                message: `Divorce Eligibility Guard eligibility success ${FlowTypes.DIVORCE} ${payload.accountFromType} account`,
                eligibleFromType: payload.accountFromType,
                code: LoggerCodes.GENERAL_SUCCESS,
              },
            }),
          );
          this.globalStore.dispatch(closeSpinner());
          this.logIneligibleAccounts(
            'INELIGIBLE_ACCOUNTS_FROM',
            FlowTypes.DIVORCE,
            payload.accountFromType,
            accountToType,
            this.rejectedFromAccounts,
          );
          // If the client is ineligible, go to the ineligible page along with logging/tracking error
        } else {
          this.globalStore.dispatch(Go({ path: [`${urlMapper[FlowTypes.DIVORCE]}/ineligible`] }));

          let message = 'No eligible divorce accounts found';

          if (!!payload.ineligibleFromAccountType) {
            this.globalStore.dispatch(
              TrackProcessError({
                errorCode: `coo-divorce-ineligible-from-${payload.ineligibleFromAccountType}_account`,
              }),
            );
            message = `No eligible Divorce ${payload.ineligibleFromAccountType} FROM account`;
          }

          this.globalStore.dispatch(
            LogError({
              payload: {
                eventId: EventId.INELIGIBLE_PAGE_ROUTE,
                message,
                ineligibleFromAccountType: payload.ineligibleFromAccountType,
                ineligibleToAccountType: payload.ineligibleToAccountType,
                flowType: FlowTypes.DIVORCE,
                rejectedFromAccounts: this.maskRejectedAccountsPII(this.rejectedFromAccounts),
              },
            }),
          );
        }

        return of(payload.isInternalTransferEligible);
      }),
      catchError((err) => {
        this.globalStore.dispatch(Go({ path: ['error'] }));
        this.globalStore.dispatch(
          LogError({
            payload: {
              eventId: 'divorce-guard--unknown-error',
              message: `Divorce Eligibility Guard failed due to unknown error. ${err.message}`,
              code: LoggerCodes.NULL_FAILURE,
            },
          }),
        );
        this.globalStore.dispatch(
          TrackProcessError({
            errorCode: `coo-divorce-ineligible-app_error`,
          }),
        );

        return of(false);
      }),
    );
  }

  private maskRejectedAccountsPII(
    rejectedAccounts: RejectedAccount[],
  ): Pick<RejectedAccount, 'accountNumber' | 'accountType' | 'rejectionReason'>[] {
    return rejectedAccounts.map((account) => ({
      accountNumber: account.accountNumber.slice(-4).padStart(account.accountNumber.length, '*'),
      accountType: account.accountType,
      rejectionReason: account.rejectionReason,
    }));
  }

  private logIneligibleAccounts(
    eventId: string,
    flowType: FlowTypes,
    selectedFromType: AccountTypes | null | undefined,
    selectedToType: AccountTypes | null,
    rejectedAccounts: RejectedAccount[],
  ): void {
    if (rejectedAccounts.length === 0) return;

    const batchSize = 200;
    const totalRejected = rejectedAccounts.length;
    const totalBatches = Math.ceil(totalRejected / batchSize);
    const maskedAccounts = this.maskRejectedAccountsPII(rejectedAccounts);

    for (let i = 0; i < totalBatches; i++) {
      this.globalStore.dispatch(
        LogInfo({
          payload: {
            eventId,
            message: `Ineligible accounts logged for ${eventId}`,
            flowType,
            code: LoggerCodes.GENERAL_SUCCESS,
            data: {
              selectedFromType: selectedFromType || '',
              selectedToType: selectedToType || '',
              totalRejectedCount: totalRejected,
              batchNumber: i + 1,
              totalBatches,
              rejectedAccounts: maskedAccounts.slice(i * batchSize, (i + 1) * batchSize),
            },
          },
        }),
      );
    }
  }
}
